import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { Redirect, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import queryString from 'query-string';

import { Theme } from 'ui_system/theme';
import PasswordValidator from 'ui_system/forms/PasswordValidator';
import CheckmarkIcon from 'views/ui/icons/checkmark-icon';

import { resetPassword } from 'data_system/token_auth/actions';
import {
  getResetPWError,
  getSubmitting,
  getBrand,
} from 'data_system/token_auth/selectors';
import {
  setHeaders,
  deleteHeaders,
  passwordValidationRules,
  isValidPassword,
} from 'data_system/token_auth/utils';
import { t } from 'services/translation';

import { EyeIcon } from 'views/ui/icons/anyfont-icons/EyeIcon';
import { EyeOffIcon } from 'views/ui/icons/anyfont-icons/EyeOffIcon';
import { LOGIN } from './routes';

import { Button } from './elements/Button';
import { FormFooter } from './FormFooter';
import {
  Label,
  Input,
  Heading,
  Form,
  FormContainer,
  Error,
  InputSection,
} from './styles_auth';
import { InvalidToken } from './InvalidToken';

const StyledButton = styled(Button)`
  margin: 32px 0px;
`;

const TermsWrapper = styled.div`
  display: flex;
  padding-top: 20px;
`;

const CheckboxWrapper = styled.div``;

const Icon = styled(CheckmarkIcon)`
  height: 7px;
  min-width: 10px;
  fill: ${Theme.colors.white};
  margin-left: 6px;
  margin-top: 7px;
`;

const EyeButton = styled.button`
  position: absolute;
  right: 5px;
  padding: 14px 5px 12px 2px;
`;

const HiddenCheckbox = styled.input`
  position: absolute;
  opacity: 0;
  cursor: pointer;
  width: 24px;
  height: 24px;
`;

const StyledCheckbox = styled.div`
  display: inline-block;
  width: 24px;
  height: 24px;
  border: 1px solid ${Theme.colors.borderActionableFieldColor};
  background: ${(props) =>
    props.checked ? Theme.colors.blue : Theme.colors.white};
  transition: all 150ms;

  ${Icon} {
    visibility: ${(props) => (props.checked ? 'visible' : 'hidden')};
  }
`;

const CheckBoxLabel = styled.label`
  flex: 2;
  padding-left: 16px;
  a {
    text-decoration: underline;
    color: ${Theme.colors.secondaryTextColor};
  }
`;

const SignUpPage = ({ submitText = 'Sign Up', userName }) => {
  const dispatch = useDispatch();
  const { search } = useLocation();

  const queryParams = queryString.parse(search);
  const { invalid, uid = '', lang = 'en-US' } = queryParams;

  const email = uid.split('+')[0];

  const [password, setPassword] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const error = useSelector(getResetPWError);
  const submitting = useSelector(getSubmitting);
  const { name } = useSelector(getBrand);

  useEffect(() => {
    document.title = `${name} - ${t('CREATE_A_PASSWORD')}`;
  }, [name]);

  useEffect(() => {
    deleteHeaders();
  }, []);

  // Early return must come after all hooks to comply with rules-of-hooks
  if (invalid) return <InvalidToken lang={lang} />;

  const handleSubmit = (e) => {
    e.preventDefault();
    setHeaders(queryParams);
    dispatch(resetPassword(password));
  };

  if (!email) {
    return <Redirect to={LOGIN} />;
  }

  const passwordRules = passwordValidationRules(password);
  const isValid = isValidPassword(password, passwordRules) && termsAccepted;

  return (
    <FormContainer>
      {userName && <Heading>{userName}</Heading>}
      <Heading bold>{t('CREATE_A_PASSWORD')}</Heading>

      <Form onSubmit={handleSubmit}>
        <Label>{t('EMAIL_ADDRESS')}</Label>
        <InputSection>
          <Input type="text" placeholder={email} disabled />
        </InputSection>
        <Label>{t('PASSWORD')}</Label>
        <InputSection>
          <Input
            onChange={(e) => setPassword(e.target.value)}
            type={showPassword ? 'text' : 'password'}
            value={password}
            placeholder={t('CHOOSE_A_PASSWORD')}
          />
          <EyeButton
            type="button"
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? (
              <EyeOffIcon color={Theme.colors.previewIconColor} />
            ) : (
              <EyeIcon color={Theme.colors.previewIconColor} />
            )}
          </EyeButton>
          {error ? <Error>{t(error)}</Error> : null}
        </InputSection>
        <PasswordValidator password={password} passwordRules={passwordRules} />
        <TermsWrapper>
          <CheckboxWrapper onClick={() => setTermsAccepted(!termsAccepted)}>
            <HiddenCheckbox checked={termsAccepted} />
            <StyledCheckbox checked={termsAccepted}>
              <Icon />
            </StyledCheckbox>
          </CheckboxWrapper>
          <CheckBoxLabel htmlFor="terms-and-conditions">
            {t('PLEASE_CONFIRM_THAT_YOU_HAVE_READ_AND_ACCEPT_OUR', '', {
              link: '',
            })}{' '}
            <a
              href="https://www.anyroad.com/end-user-terms"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('TERMS_OF_SERVICE')}
            </a>{' '}
            {t('AND')}{' '}
            <a
              href="https://www.anyroad.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('PRIVACY_POLICY')}
            </a>
            .
          </CheckBoxLabel>
        </TermsWrapper>
        <StyledButton
          disabled={submitting}
          faded={!isValid}
          text={t(submitText)}
        />
      </Form>
      <FormFooter />
    </FormContainer>
  );
};

export default SignUpPage;
